import React, { useState, useEffect } from 'react';
import clientAxios from '../../../clients/clientAxios';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import './SalesGoals.css';

interface ProgressData {
  target: number;
  current: number;
  cuota1: number;
  percentage: number;
}

const ISO_DATE_TIME_SEPARATOR = 'T';
const DISPLAY_LABELS = {
  TOTAL: 'Total',
  CUOTA1: 'Cuota 1'
} as const;
const formatDateToISO = (date: Date): string => {
  return date.toISOString().split(ISO_DATE_TIME_SEPARATOR)[0];
};
const formatMoney = (num: number): string => `$${num.toLocaleString('es-AR')}`;

export default function SalesGoals() {
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isShowingCuota1, setIsShowingCuota1] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const targetRes = await clientAxios.get('/metric-targets', {
          params: { kind: 'customer-care-sales' }
        });
        const targetValue = targetRes.data.value;
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const since = formatDateToISO(firstDay);
        const to = formatDateToISO(lastDay);
        const incomeRes = await clientAxios.post('/bills/graphic', {
          display: 'specific',
          since,
          to
        });

        const current = incomeRes.data.data[0]?.IngresosTotales || 0;
        const cuota1 = incomeRes.data.data[0]?.cuota1 || 0;
        const percentage = targetValue > 0 ? (current / targetValue) * 100 : 0;

        if (isMounted) {
          setProgressData({
            target: targetValue,
            current,
            cuota1,
            percentage: Math.round(percentage * 100) / 100
          });
        }
      } catch (err: any) {
        if (isMounted) {
          if (err.response?.status === 404) {
            setError('No hay objetivo configurado');
          } else {
            setError('Error al cargar los datos');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchSalesGoals = async () => {
    setLoading(true);
    setError(null);
    try {
      const targetRes = await clientAxios.get('/metric-targets', {
        params: { kind: 'customer-care-sales' }
      });
      const targetValue = targetRes.data.value;

      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const since = formatDateToISO(firstDay);
      const to = formatDateToISO(lastDay);
      const incomeRes = await clientAxios.post('/bills/graphic', {
        display: 'specific',
        since,
        to
      });

      const current = incomeRes.data.data[0]?.IngresosTotales || 0;
      const cuota1 = incomeRes.data.data[0]?.cuota1 || 0;
      const percentage = targetValue > 0 ? (current / targetValue) * 100 : 0;

      setProgressData({
        target: targetValue,
        current,
        cuota1,
        percentage: Math.round(percentage * 100) / 100
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No hay objetivo configurado');
      } else {
        setError('Error al cargar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sales-goals-loading">
        <div className="loading-icon">⏳</div>
        <p>Cargando objetivos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sales-goals-error">
        <p className="error-message">{error}</p>
        <button onClick={fetchSalesGoals} className="retry-button">
          Reintentar
        </button>
      </div>
    );
  }

  if (!progressData) return null;

  const displayValue = isShowingCuota1 ? progressData.cuota1 : progressData.current;
  const maxValue = Math.max(progressData.target, displayValue);
  const month = moment().format('MMMM');
  const currentHeight = (displayValue / maxValue) * 100;
  const yLabels = [
    maxValue,
    maxValue * 0.75,
    maxValue * 0.5,
    maxValue * 0.25,
    0
  ];

  return (
    <div className="sales-goals-container">
      <div className="sales-goals-header">
        <h1 className="sales-goals-title">Objetivos</h1>
        <button onClick={fetchSalesGoals} className="refresh-button">
          <FontAwesomeIcon icon={faRotate as any} /> Actualizar
        </button>
      </div>
      
      <div className="toggle-container">
        <span 
          className="toggle-text" 
          onClick={() => setIsShowingCuota1(!isShowingCuota1)}
        >
          {isShowingCuota1 ? DISPLAY_LABELS.CUOTA1 : DISPLAY_LABELS.TOTAL}
        </span>
        <label className="toggle-switch">
          <input 
            type="checkbox" 
            checked={isShowingCuota1}
            onChange={(event) => setIsShowingCuota1(event.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
      <p>
        <strong>Objetivo de ventas:</strong>{' '}
        {formatMoney(progressData.target)}
      </p>

      <p>
        <strong>Porcentaje para completar objetivo:</strong>{' '}
        {progressData.percentage.toFixed(0)}%
      </p>

      <div className="sales-goals-chart">
        <div className="y-axis-labels">
          {yLabels.map((y) => (
            <span key={y}>{formatMoney(y)}</span>
          ))}
        </div>

        <div className="chart-wrapper">
          <div className="chart-bar">
            <div className="progress-fill" style={{ height: `${currentHeight}%` }} />

            <div 
              className="target-icon" 
              style={{ bottom: `${currentHeight}%` }}
              title={`${formatMoney(displayValue)}`}
            >
              🎯
            </div>

            {yLabels.slice(1).map((_, i) => (
              <div key={i} className="grid-line" style={{ bottom: `${(i + 1) * 25}%` }} />
            ))}
          </div>

          <div className="month-label">{month}</div>
        </div>
      </div>
    </div>
  );
}
